import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOrganizationStore } from "@/lib/organization-store";
import {
  getSlackIntegration,
  getWebhooks,
  getProjectStatusPages,
  getOrganizationMembers,
} from "@/api/project-api";

interface ChecklistItem {
  key: string;
  label: string;
  completed: boolean;
  href: string;
}

interface OnboardingChecklist {
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  dismissed: boolean;
  dismiss: () => void;
}

const TOTAL_ITEMS = 4;

export function useOnboardingChecklist(
  projectId: string,
  monitorCount: number,
): OnboardingChecklist {
  const { onboardingChecklistDismissed, setOnboardingChecklistDismissed } =
    useOrganizationStore();

  const dismissed = onboardingChecklistDismissed;

  const { data: slackData } = useQuery({
    queryKey: ["slack-integration"],
    queryFn: async () => {
      const res = await getSlackIntegration();
      if (res.error) throw new Error("Failed to fetch Slack integration");
      return res.data!.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !dismissed,
  });

  const { data: webhooksData } = useQuery({
    queryKey: ["webhooks"],
    queryFn: async () => {
      const res = await getWebhooks();
      if (res.error) throw new Error("Failed to fetch webhooks");
      return res.data!.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !dismissed,
  });

  const { data: statusPagesData } = useQuery({
    queryKey: ["status-pages", projectId],
    queryFn: async () => {
      const res = await getProjectStatusPages({
        path: { projectId },
      });
      if (res.error) throw new Error("Failed to fetch status pages");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !dismissed && !!projectId,
  });

  const { data: membersData } = useQuery({
    queryKey: ["organization", "members"],
    queryFn: async () => {
      const res = await getOrganizationMembers();
      if (res.error) throw new Error("Failed to fetch members");
      return res.data!.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !dismissed,
  });

  const items = useMemo<ChecklistItem[]>(() => {
    if (dismissed) return [];

    const hasMonitor = monitorCount > 0;
    const hasNotification = !!slackData || (webhooksData ?? []).length > 0;
    const hasStatusPage = (statusPagesData?.data ?? []).length > 0;
    const hasTeamMember = (membersData ?? []).length > 1;

    return [
      {
        key: "monitor",
        label: "Create your first monitor",
        completed: hasMonitor,
        href: `/projects/${projectId}/monitors/new`,
      },
      {
        key: "notification",
        label: "Set up notifications",
        completed: hasNotification,
        href: "/settings/integrations",
      },
      {
        key: "status-page",
        label: "Create a status page",
        completed: hasStatusPage,
        href: `/projects/${projectId}/status-pages/new`,
      },
      {
        key: "team",
        label: "Invite a team member",
        completed: hasTeamMember,
        href: "/settings/members",
      },
    ];
  }, [
    dismissed,
    monitorCount,
    slackData,
    webhooksData,
    statusPagesData,
    membersData,
    projectId,
  ]);

  const completedCount = items.filter((i) => i.completed).length;

  return {
    items,
    completedCount,
    totalCount: TOTAL_ITEMS,
    dismissed,
    dismiss: () => setOnboardingChecklistDismissed(true),
  };
}
